import { useEffect, useMemo, useState } from "react";
import {
  DurableObjectSource,
  SnapshotBuffer,
  type WebSocketLike,
} from "@radar-sim/engine";
import { getAreaData } from "@radar-sim/area-data";
import type { Scenario } from "@radar-sim/schema";
import { GarminGPSMAP } from "../chrome/GarminGPSMAP.js";
import { PPI } from "../ppi/PPI.js";
import { DEFAULT_DISPLAY_STATE, type DisplayState } from "../ppi/displayState.js";
import { DEFAULT_API, buildSessionWebSocketUrl } from "./api.js";
import { InstructorConsole } from "./InstructorConsole.js";

export interface ClassroomViewProps {
  email: string;
  sessionId: string;
  scenarioId: string;
  role: "instructor" | "student";
  scenarios: ReadonlyArray<Scenario>;
  onLeave: () => void;
}

/**
 * Classroom session view. Builds a `DurableObjectSource` over a real
 * WebSocket, mounts the PPI inside the Garmin chrome, and renders the
 * instructor console alongside if the user's role is instructor.
 */
export function ClassroomView(props: ClassroomViewProps): JSX.Element {
  const [display, setDisplay] = useState<DisplayState>(DEFAULT_DISPLAY_STATE);
  const [paused, setPaused] = useState(false);
  const [connectionState, setConnectionState] = useState<string>("idle");

  const source = useMemo(() => {
    const wsUrl = buildSessionWebSocketUrl(DEFAULT_API, props.sessionId);
    const factory = (): WebSocketLike => {
      const sock = new WebSocket(wsUrl);
      return sock as unknown as WebSocketLike;
    };
    return new DurableObjectSource({ factory });
  }, [props.sessionId]);

  const buffer = useMemo(
    () => new SnapshotBuffer({ capacitySeconds: 300, tickRateHz: 10 }),
    [],
  );

  useEffect(() => {
    const stop1 = source.subscribe((s) => buffer.push(s));
    const stop2 = source.onStateChange((s) => setConnectionState(s));
    void source.connect();
    return () => {
      stop1();
      stop2();
      void source.disconnect();
    };
  }, [source, buffer]);

  const scenario = props.scenarios.find(
    (s) => s.metadata.id === props.scenarioId,
  );
  const area =
    scenario !== undefined ? getAreaData(scenario.metadata.area) : undefined;

  const togglePause = async () => {
    const next = !paused;
    setPaused(next);
    await source.sendCommand({ kind: next ? "pause" : "resume" });
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1280,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontSize: 13,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            display: "inline-block",
            padding: "2px 10px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            background:
              props.role === "instructor"
                ? "rgba(56,189,248,0.15)"
                : "var(--bg-elev)",
            color:
              props.role === "instructor"
                ? "var(--accent)"
                : "var(--text-muted)",
          }}
        >
          {props.role}
        </span>
        <span style={{ color: "var(--text-muted)" }}>
          session{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>
            {props.sessionId.slice(0, 8)}…
          </code>
        </span>
        <span style={{ color: "var(--text-muted)" }}>
          link <strong style={{ color: "var(--text)" }}>{connectionState}</strong>
        </span>
        <span style={{ color: "var(--text-muted)" }}>
          buffer{" "}
          <strong style={{ color: "var(--text)" }}>
            {buffer.length}/{buffer.capacityLimit}
          </strong>
        </span>
        <button onClick={props.onLeave} style={leaveButton}>
          Leave session
        </button>
      </div>
      <GarminGPSMAP
        ppi={
          <PPI
            source={source}
            area={area}
            scenarioId={props.scenarioId}
            display={display}
            onDisplayChange={setDisplay}
          />
        }
        display={display}
        onDisplayChange={setDisplay}
        scenarioName={scenario?.metadata.name ?? props.scenarioId}
        paused={paused}
        onPause={togglePause}
        onResume={togglePause}
      />
      {props.role === "instructor" && (
        <InstructorConsole
          source={source}
          paused={paused}
          onTogglePause={togglePause}
        />
      )}
    </div>
  );
}

const leaveButton: React.CSSProperties = {
  background: "transparent",
  color: "var(--danger)",
  border: "1px solid var(--danger)",
  borderRadius: "var(--radius-sm)",
  padding: "5px 12px",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  marginLeft: "auto",
};
